import api from "./api";
import { Matter } from "../models/Matter";
import { File } from "../models/File";
import { Event } from "../models/Event";
import { Participant } from "../models/Participant";
import { Task } from "../models/Task";
import { Question } from "../models/Question";
import { Activity } from "../models/Activity";
import { Folder } from "../models/Folder";
import { Document } from "../models/Document";
import { Analysis } from "../models/Analysis";
import { DiscoveryRequest } from "../models/DiscoveryRequest";
import { Comment } from "../models/Comment";
import { TeamMember } from "../models/TeamMember";
import { Conversation } from "../models/Conversation";
import { Filing } from "../models/Filing";
import { Import } from "../models/Import";
import { FinancialItem } from "../models/FinancialItem";
import { CreateMatterForm } from "../models/CreateMatterForm";
import { create } from "domain";
import { HolidaySchedule } from "../models/HolidaySchedule";
import { HolidayRule } from "../models/HolidayRule";
import { VisitationSchedule } from "../models/VisitationSchedule";
import { get } from "http";

const PATH = "/api/v1/matters";

export const MatterService = {
  async createMatter(matterData: CreateMatterForm): Promise<string> {
    const res = await api.post(PATH, matterData);
    return res.data.data._id;
  },

  async getAllMatters(): Promise<Matter[]> {
    const res = await api.get(PATH);
    return res.data.data;
  },

  async getMatter(matterId: string): Promise<Matter | null> {
    const res = await api.get(`${PATH}/${matterId}`);
    return res.data.data ?? null;
  },

  async updateMatter(
    matterId: string,
    updatedData: Partial<Omit<Matter, "_id" | "createdAt">>,
  ): Promise<void> {
    await api.patch(`${PATH}/${matterId}`, updatedData);
  },

  async deleteMatter(matterId: string): Promise<void> {
    await api.delete(`${PATH}/${matterId}`);
  },

  async getEvents(matterId: string): Promise<Event[]> {
    const res = await api.get(`${PATH}/${matterId}/events`);
    return res.data.data || [];
  },

  async getEventTags(matterId: string): Promise<any[]> {
    const res = await api.get(`${PATH}/${matterId}/event-tags`);
    return res.data.data || [];
  },

  async getParticipants(matterId: string): Promise<Participant[]> {
    const res = await api.get(`${PATH}/${matterId}/participants`);
    return res.data.data || [];
  },

  async getMatterFiles(matterId: string): Promise<File[]> {
    const res = await api.get(`${PATH}/${matterId}/files`);
    return res.data.data || [];
  },

  async getFolders(matterId: string): Promise<Folder[]> {
    const res = await api.get(`${PATH}/${matterId}/folders`);
    return res.data.data || [];
  },

  async getDocuments(matterId: string): Promise<Document[]> {
    const res = await api.get(`${PATH}/${matterId}/documents`);
    return res.data.data || [];
  },

  async getTasks(matterId: string): Promise<Task[]> {
    const res = await api.get(`${PATH}/${matterId}/tasks`);
    return res.data.data || [];
  },

  async getQuestions(matterId: string): Promise<Question[]> {
    const res = await api.get(`${PATH}/${matterId}/questions`);
    return res.data.data || [];
  },

  async getActivities(matterId: string): Promise<Activity[]> {
    const res = await api.get(`${PATH}/${matterId}/activities`);
    return res.data.data || [];
  },

  async getFiles(matterId: string): Promise<File[]> {
    const res = await api.get(`${PATH}/${matterId}/files`);
    return res.data.data || [];
  },

  async getDiscoveryRequests(matterId: string): Promise<DiscoveryRequest[]> {
    const res = await api.get(`${PATH}/${matterId}/discovery-requests`);
    return res.data.data || [];
  },

  async getLatestAnalysis(matterId: string): Promise<Analysis | null> {
    const res = await api.get(`${PATH}/${matterId}/latest-analysis`);
    return res.data.data;
  },

  async getComments(matterId: string): Promise<Comment[]> {
    const res = await api.get(`${PATH}/${matterId}/comments`);
    return res.data.data || [];
  },

  async getTeamMembers(matterId: string): Promise<TeamMember[]> {
    const res = await api.get(`${PATH}/${matterId}/memberships`);
    return res.data.data || [];
  },

  async getConversations(matterId: string): Promise<Conversation[]> {
    const res = await api.get(`${PATH}/${matterId}/conversations`);
    return res.data.data || [];
  },

  async getFilings(matterId: string): Promise<Filing[]> {
    const res = await api.get(`${PATH}/${matterId}/filings`);
    return res.data.data || [];
  },

  async getImports(matterId: string): Promise<Import[]> {
    const res = await api.get(`${PATH}/${matterId}/imports`);
    return res.data.data || [];
  },

  async getFinancialItems(matterId: string): Promise<FinancialItem[]> {
    const res = await api.get(`${PATH}/${matterId}/financial-items`);
    return res.data.data || [];
  },

  async getChildSupportOrders(matterId: string): Promise<any[]> {
    const res = await api.get(`${PATH}/${matterId}/child-support-orders`);
    return res.data.data || [];
  },

  async getChildSupportPayments(matterId: string): Promise<any[]> {
    const res = await api.get(`${PATH}/${matterId}/child-support-payments`);
    return res.data.data || [];
  },

  async getSpousalSupportOrders(matterId: string): Promise<any[]> {
    const res = await api.get(`${PATH}/${matterId}/spousal-support-orders`);
    return res.data.data || [];
  },

  async getSpousalSupportPayments(matterId: string): Promise<any[]> {
    const res = await api.get(`${PATH}/${matterId}/spousal-support-payments`);
    return res.data.data || [];
  },

  async getInvitations(matterId: string): Promise<any[]> {
    const res = await api.get(`${PATH}/${matterId}/invitations`);
    return res.data.data || [];
  },

  // Holiday Schedule
  async getHolidaySchedules(matterId: string): Promise<any[]> {
    const res = await api.get(`${PATH}/${matterId}/holiday-schedules`);
    return res.data.data || [];
  },
  async createHolidaySchedule(
    matterId: string,
    data: Omit<HolidaySchedule, "_id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const res = await api.post(`${PATH}/${matterId}/holiday-schedules`, data);
    return res.data.data._id;
  },
  async createHolidayRule(
    matterId: string,
    holidayScheduleId: string,
    data: Omit<HolidayRule, "_id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const res = await api.post(
      `${PATH}/${matterId}/holiday-schedules/${holidayScheduleId}/holiday-rules`,
      data,
    );
    return res.data.data._id;
  },

  // Visitation Schedule
  async getVisitationSchedules(matterId: string): Promise<any[]> {
    const res = await api.get(`${PATH}/${matterId}/visitation-schedules`);
    return res.data.data || [];
  },

  async createVisitationSchedule(
    matterId: string,
    data: Omit<VisitationSchedule, "_id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const res = await api.post(
      `${PATH}/${matterId}/visitation-schedules`,
      data,
    );
    return res.data.data._id;
  },

  async unlockFeature(matterId: string, featureType: string): Promise<void> {
    await api.post(`${PATH}/${matterId}/unlock-feature/${featureType}`);
  },
};
